/**
 * Sliding-session token refresh helpers.
 * Keeps users logged in as long as they open the app within their session window.
 */
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_URL } from '../config/api';

const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000; // refresh tokens older than 24h

let refreshPromise = null;

const getTokenInfo = () => {
  const token = localStorage.getItem('jwtToken');
  if (!token) return null;
  try {
    return { token, decoded: jwtDecode(token) };
  } catch (e) {
    return null;
  }
};

export const isTokenExpired = () => {
  const info = getTokenInfo();
  if (!info) return true;
  if (!info.decoded.exp) return false;
  return Date.now() >= info.decoded.exp * 1000;
};

// Returns: true = refreshed, false = refresh rejected (401), null = network/indeterminate
export const refreshSession = () => {
  if (refreshPromise) return refreshPromise;
  const info = getTokenInfo();
  if (!info || isTokenExpired()) return Promise.resolve(false);
  refreshPromise = axios
    .post(`${API_URL}/v2/auth/refresh`, {}, { headers: { Authorization: `Bearer ${info.token}` } })
    .then((res) => {
      if (res.data?.token) {
        localStorage.setItem('jwtToken', res.data.token);
        return true;
      }
      return false;
    })
    .catch((err) => {
      if (err.response?.status === 401) return false;
      return null; // network hiccup — do not log the user out
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

export const refreshSessionIfStale = () => {
  const info = getTokenInfo();
  if (!info || isTokenExpired()) return;
  const iat = info.decoded.iat;
  if (!iat || Date.now() - iat * 1000 > REFRESH_AFTER_MS) {
    refreshSession();
  }
};

// Call once on app startup: refreshes on load, when the app returns to the
// foreground (phone/PWA), and periodically for long-lived tabs.
export const initSessionKeepAlive = () => {
  refreshSessionIfStale();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshSessionIfStale();
  });
  setInterval(refreshSessionIfStale, 6 * 60 * 60 * 1000);
};
